import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { Navigate } from "react-router-dom";
import { RefreshCw, Search, Download, Calendar, AlertTriangle, CheckCircle, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";

type EmailEvent = Tables<"email_events">;

export default function OpsEventsPage() {
  const { user, tenantType } = useAuth();
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [tenantFilter, setTenantFilter] = useState<string>("all");
  const [selectedEvent, setSelectedEvent] = useState<EmailEvent | null>(null);

  // Redirect non-admin users
  if (tenantType !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchEvents = async () => {
    try {
      setRefreshing(true);
      
      let query = supabase
        .from("email_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1000); // Limit to recent 1000 events

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter as "queued" | "processing" | "success" | "faulted");
      }

      if (tenantFilter !== "all") {
        query = query.eq("tenant_id", tenantFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [statusFilter, tenantFilter]);

  const handleRefresh = () => {
    fetchEvents();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "faulted":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "processing":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "queued":
        return <Clock className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "success":
        return "default";
      case "faulted":
        return "destructive";
      case "processing":
        return "secondary";
      case "queued":
        return "outline";
      default:
        return "outline";
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch = 
      event.message_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.tenant_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.correlation_id && event.correlation_id.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const uniqueTenants = [...new Set(events.map(e => e.tenant_id))];

  const exportEvents = () => {
    const csvContent = [
      ["Timestamp", "Status", "Tenant ID", "Message ID", "Correlation ID", "Error Details"],
      ...filteredEvents.map(event => [
        event.created_at,
        event.status,
        event.tenant_id,
        event.message_id,
        event.correlation_id || "",
        event.error_details || ""
      ])
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `email_events_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success("Events exported successfully");
  };

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Event Logs</h1>
          <p className="text-muted-foreground">Cross-tenant email event monitoring</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportEvents} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by message ID, tenant ID, or correlation ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="faulted">Faulted</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tenantFilter} onValueChange={setTenantFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tenants</SelectItem>
                {uniqueTenants.map(tenant => (
                  <SelectItem key={tenant} value={tenant}>{tenant}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Recent Events ({filteredEvents.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredEvents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No events found</p>
              ) : (
                filteredEvents.map((event) => (
                  <div
                    key={event.id}
                    className={`p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedEvent?.id === event.id ? "border-primary bg-muted/30" : ""
                    }`}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(event.status)}
                        <Badge variant={getStatusBadgeVariant(event.status)}>
                          {event.status}
                        </Badge>
                        <span className="text-sm font-medium">
                          {event.message_id.substring(0, 20)}...
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(event.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      Tenant: {event.tenant_id}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Event Details Modal/Panel */}
      {selectedEvent && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Event Details
              <Button variant="outline" size="sm" onClick={() => setSelectedEvent(null)}>
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusIcon(selectedEvent.status)}
                    <Badge variant={getStatusBadgeVariant(selectedEvent.status)}>
                      {selectedEvent.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Message ID</label>
                  <p className="mt-1 font-mono text-sm break-all">{selectedEvent.message_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Tenant ID</label>
                  <p className="mt-1 font-mono text-sm">{selectedEvent.tenant_id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Correlation ID</label>
                  <p className="mt-1 font-mono text-sm">{selectedEvent.correlation_id || "N/A"}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created At</label>
                  <p className="mt-1">{new Date(selectedEvent.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Processed At</label>
                  <p className="mt-1">{selectedEvent.processed_at ? new Date(selectedEvent.processed_at).toLocaleString() : "Not processed"}</p>
                </div>
              </div>
            </div>
            
            {selectedEvent.error_details && (
              <div className="mt-4">
                <label className="text-sm font-medium text-muted-foreground">Error Details</label>
                <div className="mt-1 p-3 bg-destructive/10 border border-destructive/20 rounded">
                  <p className="text-sm text-destructive">{selectedEvent.error_details}</p>
                </div>
              </div>
            )}

            {selectedEvent.payload && (
              <div className="mt-4">
                <label className="text-sm font-medium text-muted-foreground">Payload</label>
                <pre className="mt-1 p-3 bg-muted rounded text-xs overflow-auto max-h-48">
                  {JSON.stringify(selectedEvent.payload, null, 2)}
                </pre>
              </div>
            )}

            {selectedEvent.process_log && (
              <div className="mt-4">
                <label className="text-sm font-medium text-muted-foreground">Process Log</label>
                <pre className="mt-1 p-3 bg-muted rounded text-xs overflow-auto max-h-48">
                  {JSON.stringify(selectedEvent.process_log, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}